const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');

const { default: Typography } = require('@mui/material/Typography');
const { default: Button } = require('@mui/material/Button');

const BottomButtonContainer = require('components/BottomButtonContainer');
const SimpleDialog = require('components/SimpleDialog');
const SupportPagePreventNavigationDialog = require('components/SupportPagePreventNavigationDialog');
const { analyticsTemplates } = require('utils/analytics');
const useForm = require('hooks/useForm');
const SupportFormConfig = require('./formConfig');
const { default: Classes } = require('./styles.scss');

const { useCallback, useMemo, useRef, useState } = React;

const FORM_ID = 'support-form';

const internals = {};

module.exports = function BugReportPage(props) {

    const {
        categories = [],
        currentUser,
        history,
        location,
        onClickCloseAccount,
        onInvalidSubmit,
        onSubmitBugReport,
        onSubmitBugReportNoRedirect
    } = props;

    const methodsRef = useRef(null);
    const [bugType, setBugType] = useState(() => SupportFormConfig.getInitialBugType({ location }));
    const [submitting, setSubmitting] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertMessage, setAlertMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(() => () => null);
    const redirectUrl = useMemo(() => SupportFormConfig.getInitialRedirectUrl({ location }), [location]);
    const appVersion = process.env.APP_VERSION;
    const isAdmin = currentUser?.roleId === 1 || currentUser?.roleId === 99;
    const isCloseMyAccount = SupportFormConfig.internals.isCloseMyAccount(bugType);

    React.useEffect(() => {

        window.scrollTo(0, 0);
    }, []);

    const resetCurrentForm = useCallback(() => {

        const methods = methodsRef.current;

        if (!methods || typeof methods.reset !== 'function') {
            return;
        }

        methods.reset(methods.getValues());
    }, []);

    const submitCloseAccount = useCallback((values) => {

        setAlertTitle('Close account');
        setAlertMessage('Are you sure you want to close your account? By doing so, you will no longer be able to access the platform or your college\'s Nearpeer community, and all of your connections and conversations will be lost. If you need to re-open your account later, please contact us at administrator@nearpeer.net.');
        setConfirmAction(() => () => onClickCloseAccount({
            closeAccountReason: values.bugDetails
        }));
        setAlertOpen(true);
    }, [onClickCloseAccount]);

    const submitForm = useCallback((values) => {

        analyticsTemplates.buttons('submit support', 'support: submit');

        if (SupportFormConfig.internals.isCloseMyAccount(values.bugType)) {
            submitCloseAccount(values);
            return undefined;
        }

        setSubmitting(true);

        return onSubmitBugReport(SupportFormConfig.buildSubmitPayload({ values }), redirectUrl)
            .then((result) => {

                resetCurrentForm();
                return result;
            })
            .finally(() => setSubmitting(false));
    }, [onSubmitBugReport, redirectUrl, resetCurrentForm, submitCloseAccount]);

    const submitNoRedirect = useCallback((values) => {

        analyticsTemplates.buttons('submit support', 'support: submit');

        if (SupportFormConfig.internals.isCloseMyAccount(values.bugType)) {
            submitCloseAccount(values);
            return undefined;
        }

        setSubmitting(true);

        return onSubmitBugReportNoRedirect(SupportFormConfig.buildSubmitPayload({ values }))
            .then((result) => {

                resetCurrentForm();
                return result;
            })
            .finally(() => setSubmitting(false));
    }, [onSubmitBugReportNoRedirect, resetCurrentForm, submitCloseAccount]);

    const defaults = useMemo(() => SupportFormConfig.internals.buildDefaults({
        bugType,
        redirectUrl
    }), [bugType, redirectUrl]);

    const formConfig = useMemo(() => SupportFormConfig.buildFormConfig({
        categories,
        defaults,
        formId: FORM_ID,
        isAdmin,
        bugType,
        onBugTypeChange: setBugType,
        onInvalidSubmit,
        onSubmit: submitForm
    }), [categories, defaults, isAdmin, bugType, onInvalidSubmit, submitForm]);

    const {
        methods,
        Form
    } = useForm(formConfig);

    methodsRef.current = methods;

    const saveWithoutRedirect = useMemo(() => methods.handleSubmit(submitNoRedirect, onInvalidSubmit), [methods, onInvalidSubmit, submitNoRedirect]);
    const pageHasErrors = Object.keys(methods.formState.errors || {}).length > 0;
    const pageIsSubmitting = submitting || methods.formState.isSubmitting;
    const shouldShowPreventNavigationDialog = methods.formState.isDirty && !pageIsSubmitting;

    const {
        MainContainer,
        ContentContainer,
        InnerWrapper
    } = internals;

    return <MainContainer>
        <ContentContainer>
            <BottomButtonContainer
                btnLabel='Submit'
                disabled={pageIsSubmitting}
                formId={FORM_ID}
            >
                <InnerWrapper>
                    {isCloseMyAccount ? <SimpleDialog
                        isOpen={alertOpen}
                        onRequestClose={() => setAlertOpen(false)}
                        title={alertTitle}
                        content={alertMessage}
                        buttons={<React.Fragment>
                            <Button
                                onClick={() => setAlertOpen(false)}
                                color='secondary'
                                variant='contained'
                                data-focus-outline='radius:40'
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => {
                                    setAlertOpen(false);
                                    confirmAction();
                                }}
                                color='primary'
                                variant='contained'
                                data-focus-outline='radius:40'
                            >
                                Ok
                            </Button>
                        </React.Fragment>}
                    /> : <SupportPagePreventNavigationDialog
                        saveFunction={saveWithoutRedirect}
                        pageHasErrors={pageHasErrors}
                        alertMsg={'Your Support form has not yet been submitted. Do you want to leave this page or stay?'}
                        when={shouldShowPreventNavigationDialog}
                        navigate={(path, state) => history.push(path, state)}
                        shouldBlockNavigation={() => shouldShowPreventNavigationDialog}
                    />}
                    <Typography
                        variant='h2'
                        style={{
                            marginTop: 10,
                            marginBottom: 16
                        }}
                    >
                        Support
                    </Typography>
                    {Form}
                </InnerWrapper>
            </BottomButtonContainer>
        </ContentContainer>
        <div className={Classes.appVersionWrapper}>
            {`Nearpeer v${appVersion} `}&copy;{new Date().getFullYear()}
        </div>
    </MainContainer>;
};

module.exports.propTypes = {
    categories: T.array.isRequired,
    currentUser: T.object,
    history: T.object,
    location: T.object,
    onClickCloseAccount: T.func,
    onInvalidSubmit: T.func,
    onSubmitBugReport: T.func,
    onSubmitBugReportNoRedirect: T.func
};

internals.MainContainer = Styled.div`
    display: flex;
    flex-flow: column nowrap;
    width: 100%;
    height: 100%;

    header {
        top: var(--safezonetop);
    }
`;

internals.ContentContainer = Styled.div`
    flex-grow: 1;
`;

internals.InnerWrapper = Styled.div`
    width: 100%;
    padding: 1.5em;
    display: flex;
    flex-flow: column nowrap;

    & > div {
        width: 100% !important;
    }

    h2 {
        margin-top: 0;
    }
`;

module.exports.internals = internals;
